import React from 'react'
import { useNavigate } from 'react-router-dom'

// material-ui
import { 
    Button, Grid, InputLabel, 
    OutlinedInput, Stack, Alert, Typography, 
    IconButton, Box, InputAdornment, Select, MenuItem,
    Paper, Card, CardContent
} from '@mui/material'
import { Add as AddIcon, Delete as DeleteIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material'
import * as Yup from 'yup'
import { Formik, FieldArray } from 'formik'

// project import
import AnimateButton from 'components/@extended/AnimateButton'
import { FormFieldError } from 'components/Form'
import { useSnackbar } from 'notistack'
import SelectInput from 'components/inputs/SelectInput'
import DynamicAutocomplete from 'components/inputs/Autocomplete/DynamicAutocomplete'
import { orderCreate } from 'models/order'
import { customerList } from 'models/customer'
import { productList } from 'models/product'

// Order status options
const OrderStatusOptions = [
    { label: 'Pending', value: 'pending' },
    { label: 'Confirmed', value: 'confirmed' },
    { label: 'Processing', value: 'processing' },
    { label: 'Delivered', value: 'delivered' },
    { label: 'Cancelled', value: 'cancelled' }
]

// Discount type options
const DiscountTypeOptions = [
    { label: 'Percentage (%)', value: 'percentage' },
    { label: 'Fixed Amount', value: 'fixed' }
]

// Order creation form validation rules
const validation = Yup.object().shape({
    customer_id: Yup.string().required('Customer is required'),
    status: Yup.string().required('Status is required'),
    delivery_date: Yup.date().nullable(),
    notes: Yup.string().nullable(),
    order_items: Yup.array().of(
        Yup.object().shape({
            product_id: Yup.string().required('Product is required'),
            quantity: Yup.number().min(1, 'Quantity must be at least 1').required('Quantity is required'),
            unit_price: Yup.number().min(0, 'Price must be positive').required('Unit price is required'),
            discount_type: Yup.string().nullable(),
            discount_value: Yup.number().min(0, 'Discount must be positive').nullable()
        })
    ).min(1, 'At least one item is required')
})

/*
 * Order Add Page Component
 */
const OrderAdd = () => {
    const navigate = useNavigate()
    const { enqueueSnackbar } = useSnackbar()

    // Calculate item total
    const calculateItemTotal = (item) => {
        const quantity = parseFloat(item.quantity) || 0
        const unitPrice = parseFloat(item.unit_price) || 0
        const subtotal = quantity * unitPrice
        
        let discountAmount = 0
        if (item.discount_value) {
            if (item.discount_type === 'percentage') {
                discountAmount = (subtotal * parseFloat(item.discount_value)) / 100
            } else {
                discountAmount = parseFloat(item.discount_value)
            }
        }
        
        return {
            subtotal: subtotal.toFixed(2),
            discountAmount: discountAmount.toFixed(2),
            total: (subtotal - discountAmount).toFixed(2)
        }
    }

    // Calculate order totals
    const calculateOrderTotals = (items, taxRate = 0) => {
        let totalQuantity = 0
        let netTotal = 0
        
        if (!items) {
            return {
                totalQuantity: 0,
                netTotal: '0.00',
                taxAmount: '0.00',
                grandTotal: '0.00'
            }
        }
        
        items.forEach(item => {
            totalQuantity += parseFloat(item.quantity) || 0
            const itemCalc = calculateItemTotal(item)
            netTotal += parseFloat(itemCalc.total)
        })
        
        const taxAmount = (netTotal * taxRate) / 100
        const grandTotal = netTotal + taxAmount
        
        return {
            totalQuantity,
            netTotal: netTotal.toFixed(2),
            taxAmount: taxAmount.toFixed(2),
            grandTotal: grandTotal.toFixed(2)
        }
    }

    // Send data to server
    const onSubmit = async (values, { setErrors, setSubmitting }) => {
        await orderCreate(values)
            .then((res) => {
                enqueueSnackbar(res.message || 'Order created successfully', { variant: 'success', autoHideDuration: 1500 })
                navigate('/orders')
            })
            .catch((res) => {
                if (res.errors) {
                    res.errors['submit'] = res.message
                    setErrors(res.errors)
                } else {
                    setErrors({ submit: res.message })
                }
                enqueueSnackbar(res.message || 'Failed to create order', { variant: 'error', autoHideDuration: 1500 })
            })
            .finally(() => {
                setSubmitting(false)
            })
    }

    const initialValues = {
        customer_id: '',
        status: 'pending',
        delivery_date: '',
        notes: '',
        tax_rate: 10,
        order_items: [
            {
                product_id: '',
                product_name: '',
                quantity: 1,
                unit_price: 0,
                discount_type: 'percentage',
                discount_value: 0
            }
        ]
    }

    return (
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
            {/* Page Header */}
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 4 }}>
                <IconButton 
                    onClick={() => navigate('/orders')} 
                    sx={{ 
                        bgcolor: 'background.paper',
                        boxShadow: 1,
                        '&:hover': { bgcolor: 'grey.100' }
                    }}
                >
                    <ArrowBackIcon />
                </IconButton>
                <Box>
                    <Typography variant="h3" fontWeight={600}>Create New Order</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Add order details and items
                    </Typography>
                </Box>
            </Stack>

            <Formik initialValues={initialValues} validationSchema={validation} onSubmit={onSubmit}>
                {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values, setFieldValue }) => {
                    const totals = calculateOrderTotals(values.order_items, values.tax_rate)
                    
                    return (
                        <form noValidate onSubmit={handleSubmit}>
                            <Stack spacing={3}>
                                {errors.submit && (
                                    <Alert severity="error">{errors.submit}</Alert>
                                )}
                                
                                {/* Order Information Card */}
                                <Paper elevation={0} sx={{ p: 3, bgcolor: 'background.paper', borderRadius: 2 }}>
                                    <Typography variant="h5" fontWeight={600} sx={{ mb: 3 }}>
                                        Order Information
                                    </Typography>
                                    
                                    <Grid container spacing={3}>
                                        {/* Customer Selection */}
                                        <Grid item size={{ md: 6, xs: 12 }}>
                                            <Stack spacing={1}>
                                                <InputLabel>Customer *</InputLabel>
                                                <DynamicAutocomplete
                                                    setFieldValue={(field, value) => {
                                                        setFieldValue('customer_id', value ? value.id : '')
                                                    }}
                                                    initialValue={null}
                                                    fetchList={customerList}
                                                    fieldName="customer_id"
                                                    labelKey="name"
                                                    valueKey="id"
                                                />
                                                <FormFieldError touched={touched.customer_id} errors={errors.customer_id} />
                                            </Stack>
                                        </Grid>

                                        {/* Status */}
                                        <Grid item size={{ md: 6, xs: 12 }}>
                                            <Stack spacing={1}>
                                                <InputLabel>Status *</InputLabel>
                                                <SelectInput
                                                    label="Status"
                                                    name="status"
                                                    value={values.status}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    error={errors.status}
                                                    touched={touched.status}
                                                    options={OrderStatusOptions}
                                                    required
                                                />
                                                <FormFieldError touched={touched.status} errors={errors.status} />
                                            </Stack>
                                        </Grid>

                                        {/* Delivery Date */}
                                        <Grid item size={{ md: 6, xs: 12 }}>
                                            <Stack spacing={1}>
                                                <InputLabel>Delivery Date</InputLabel>
                                                <OutlinedInput
                                                    type="date"
                                                    value={values.delivery_date}
                                                    name="delivery_date"
                                                    onBlur={handleBlur}
                                                    onChange={handleChange}
                                                    fullWidth
                                                    error={Boolean(touched.delivery_date && errors.delivery_date)}
                                                />
                                                <FormFieldError touched={touched.delivery_date} errors={errors.delivery_date} />
                                            </Stack>
                                        </Grid>

                                        {/* Tax Rate */}
                                        <Grid item size={{ md: 6, xs: 12 }}>
                                            <Stack spacing={1}>
                                                <InputLabel>Tax Rate (%)</InputLabel>
                                                <OutlinedInput
                                                    type="number"
                                                    value={values.tax_rate}
                                                    name="tax_rate"
                                                    onBlur={handleBlur}
                                                    onChange={handleChange}
                                                    fullWidth
                                                    inputProps={{ step: "0.01", min: "0" }}
                                                    endAdornment={<InputAdornment position="end">%</InputAdornment>}
                                                />
                                            </Stack>
                                        </Grid>

                                        {/* Notes */}
                                        <Grid item size={{ xs: 12 }}>
                                            <Stack spacing={1}>
                                                <InputLabel>Notes</InputLabel>
                                                <OutlinedInput
                                                    type="text"
                                                    value={values.notes}
                                                    name="notes"
                                                    onBlur={handleBlur}
                                                    onChange={handleChange}
                                                    placeholder="Enter any additional notes..."
                                                    fullWidth
                                                    multiline
                                                    rows={3}
                                                    error={Boolean(touched.notes && errors.notes)}
                                                />
                                                <FormFieldError touched={touched.notes} errors={errors.notes} />
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                </Paper>

                                {/* Order Items Section */}
                                <Paper elevation={0} sx={{ p: 3, bgcolor: 'background.paper', borderRadius: 2 }}>
                                    <Typography variant="h5" fontWeight={600} sx={{ mb: 3 }}>
                                        Order Items
                                    </Typography>
                                    
                                    <FieldArray name="order_items">
                                        {({ push, remove }) => (
                                            <Stack spacing={2}>
                                                {values.order_items.map((item, index) => {
                                                    const itemCalc = calculateItemTotal(item)
                                                    const itemErrors = errors.order_items?.[index]
                                                    const itemTouched = touched.order_items?.[index]
                                                    
                                                    return (
                                                        <Paper
                                                            key={index}
                                                            elevation={0}
                                                            sx={{
                                                                p: 2.5,
                                                                bgcolor: 'grey.50',
                                                                borderRadius: 2,
                                                            }}
                                                        >
                                                            <Stack spacing={2.5}>
                                                                {/* Item Header */}
                                                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                                    <Typography variant="subtitle1" fontWeight={600}>
                                                                        Item #{index + 1}
                                                                    </Typography>
                                                                    {values.order_items.length > 1 && (
                                                                        <IconButton
                                                                            color="error"
                                                                            size="small"
                                                                            onClick={() => remove(index)}
                                                                        >
                                                                            <DeleteIcon fontSize="small" />
                                                                        </IconButton>
                                                                    )}
                                                                </Stack>

                                                                <Grid container spacing={2}>
                                                                    {/* Product Selection */}
                                                                    <Grid item size={{ md: 3, xs: 12 }}>
                                                                        <Stack spacing={1}>
                                                                            <InputLabel>Product *</InputLabel>
                                                                            <DynamicAutocomplete
                                                                                setFieldValue={(field, value) => {
                                                                                    setFieldValue(`order_items.${index}.product_id`, value ? value.id : '')
                                                                                    setFieldValue(`order_items.${index}.product_name`, value ? value.name : '')
                                                                                    if (value?.price) {
                                                                                        setFieldValue(`order_items.${index}.unit_price`, value.price)
                                                                                    }
                                                                                }}
                                                                                initialValue={null}
                                                                                fetchList={productList}
                                                                                fieldName={`order_items.${index}.product_id`}
                                                                                labelKey="name"
                                                                                valueKey="id"
                                                                            />
                                                                            <FormFieldError 
                                                                                touched={itemTouched?.product_id} 
                                                                                errors={itemErrors?.product_id} 
                                                                            />
                                                                        </Stack>
                                                                    </Grid>

                                                                    {/* Quantity */}
                                                                    <Grid item size={{ md: 2, xs: 6 }}>
                                                                        <Stack spacing={1}>
                                                                            <InputLabel>Quantity *</InputLabel>
                                                                            <OutlinedInput
                                                                                type="number"
                                                                                value={item.quantity}
                                                                                name={`order_items.${index}.quantity`}
                                                                                onChange={handleChange}
                                                                                onBlur={handleBlur}
                                                                                fullWidth
                                                                                inputProps={{ min: "1", step: "1" }}
                                                                                error={Boolean(itemTouched?.quantity && itemErrors?.quantity)}
                                                                            />
                                                                            <FormFieldError 
                                                                                touched={itemTouched?.quantity} 
                                                                                errors={itemErrors?.quantity} 
                                                                            />
                                                                        </Stack>
                                                                    </Grid>

                                                                    {/* Unit Price */}
                                                                    <Grid item size={{md: 2, xs: 6 }}>
                                                                        <Stack spacing={1}>
                                                                            <InputLabel>Unit Price *</InputLabel>
                                                                            <OutlinedInput
                                                                                type="number"
                                                                                value={item.unit_price}
                                                                                name={`order_items.${index}.unit_price`}
                                                                                onChange={handleChange}
                                                                                onBlur={handleBlur}
                                                                                fullWidth
                                                                                inputProps={{ min: "0", step: "0.01" }}
                                                                                startAdornment={<InputAdornment position="start">AED</InputAdornment>}
                                                                                error={Boolean(itemTouched?.unit_price && itemErrors?.unit_price)}
                                                                            />
                                                                            <FormFieldError 
                                                                                touched={itemTouched?.unit_price} 
                                                                                errors={itemErrors?.unit_price} 
                                                                            />
                                                                        </Stack>
                                                                    </Grid>

                                                                    {/* Discount Type */}
                                                                    <Grid item size={{md: 2, xs: 6 }}>
                                                                        <Stack spacing={1}>
                                                                            <InputLabel>Discount Type</InputLabel>
                                                                            <Select
                                                                                value={item.discount_type}
                                                                                name={`order_items.${index}.discount_type`}
                                                                                onChange={handleChange}
                                                                                fullWidth
                                                                                size="medium"
                                                                            >
                                                                                {DiscountTypeOptions.map((option) => (
                                                                                    <MenuItem key={option.value} value={option.value}>
                                                                                        {option.label}
                                                                                    </MenuItem>
                                                                                ))}
                                                                            </Select>
                                                                        </Stack>
                                                                    </Grid>

                                                                    {/* Discount Value */}
                                                                    <Grid item size={{ md: 3, xs: 6 }}>
                                                                        <Stack spacing={1}>
                                                                            <InputLabel>Discount Value</InputLabel>
                                                                            <OutlinedInput
                                                                                type="number"
                                                                                value={item.discount_value}
                                                                                name={`order_items.${index}.discount_value`}
                                                                                onChange={handleChange}
                                                                                onBlur={handleBlur}
                                                                                fullWidth
                                                                                inputProps={{ min: "0", step: "0.01" }}
                                                                                endAdornment={
                                                                                    <InputAdornment position="end">
                                                                                        {item.discount_type === 'percentage' ? '%' : 'AED'}
                                                                                    </InputAdornment>
                                                                                }
                                                                            />
                                                                        </Stack>
                                                                    </Grid>
                                                                </Grid>
                                                            </Stack>
                                                        </Paper>
                                                    )
                                                })}

                                                {/* Add Item Button */}
                                                <Button
                                                    variant="outlined"
                                                    size="large"
                                                    startIcon={<AddIcon />}
                                                    onClick={() => push({
                                                        product_id: '',
                                                        product_name: '',
                                                        quantity: 1,
                                                        unit_price: 0,
                                                        discount_type: 'percentage',
                                                        discount_value: 0
                                                    })}
                                                    sx={{ 
                                                        alignSelf: 'flex-start',
                                                        borderStyle: 'dashed'
                                                    }}
                                                >
                                                    Add Item
                                                </Button>

                                                {typeof errors.order_items === 'string' && (
                                                    <Alert severity="error">{errors.order_items}</Alert>
                                                )}
                                            </Stack>
                                        )}
                                    </FieldArray>
                                </Paper>

                                {/* Order Summary */}
                                <Paper 
                                    elevation={0}
                                    sx={{ 
                                        p: 3,
                                        bgcolor: 'primary.100',
                                        borderRadius: 2,
                                    }}
                                >
                                    <Typography variant="h4" fontWeight={600} sx={{ mb: 3 }}>
                                        Order Summary
                                    </Typography>
                                    <Grid container spacing={3}>
                                        <Grid item size={{ md: 3, sm: 6, xs: 12 }}>
                                            <Typography variant="h5" color="text.secondary" sx={{ mb: 0.5 }}>
                                                Total Quantity
                                            </Typography>
                                            <Typography variant="h5" fontWeight={700}>
                                                {totals.totalQuantity}
                                            </Typography>
                                        </Grid>
                                        <Grid item size={{ md: 3, sm: 6, xs: 12 }}>
                                            <Typography variant="h5" color="text.secondary" sx={{ mb: 0.5 }}>
                                                Net Total
                                            </Typography>
                                            <Typography variant="h5" fontWeight={700}>
                                                AED {totals.netTotal}
                                            </Typography>
                                        </Grid>
                                        <Grid item size={{ md: 3, sm: 6, xs: 12 }}>
                                            <Typography variant="h5" color="text.secondary" sx={{ mb: 0.5 }}>
                                                Tax ({values.tax_rate}%)
                                            </Typography>
                                            <Typography variant="h5" fontWeight={700}>
                                                AED {totals.taxAmount}
                                            </Typography>
                                        </Grid>
                                        <Grid item size={{ md: 3, sm: 6, xs: 12 }}>
                                            <Typography variant="h5" color="text.secondary" sx={{ mb: 0.5 }}>
                                                Grand Total
                                            </Typography>
                                            <Typography variant="h5" fontWeight={700} color="primary.main">
                                                AED {totals.grandTotal}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Paper>

                                {/* Action Buttons */}
                                <Stack 
                                    direction="row" 
                                    spacing={2} 
                                    justifyContent="flex-end"
                                    sx={{ pt: 2 }}
                                >
                                    <Button 
                                        disabled={isSubmitting} 
                                        onClick={() => navigate('/orders')} 
                                        type="button" 
                                        variant="outlined" 
                                        color="secondary"
                                        size="large"
                                        sx={{ minWidth: 120 }}
                                    >
                                        Cancel
                                    </Button>
                                    <Button 
                                        disabled={isSubmitting} 
                                        type="submit" 
                                        variant="contained" 
                                        color="primary"
                                        size="large"
                                        sx={{ minWidth: 150 }}
                                    >
                                        {isSubmitting ? 'Creating...' : 'Create Order'}
                                    </Button>
                                </Stack>
                            </Stack>
                        </form>
                    )
                }}
            </Formik>
        </Box>
    )
}

export default OrderAdd